import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
  Radio,
  Select,
  Tag,
  Divider,
  Checkbox,
  Upload
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  MinusCircleOutlined,
  UploadOutlined,
  FileWordOutlined,
  FileAddOutlined
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makePutCall, makeDeleteCall } from '../../utils/helper';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createQuizDocument, deleteQuizDocument, forceSaveQuizDocument } from './onlyoffice/quizDocumentsApi';
import QuizDocumentEditor from './onlyoffice/QuizDocumentEditor';

const { TextArea } = Input;
const { Option } = Select;

// Helper function to decode HTML entities
const decodeHtmlEntities = (html) => {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const ManageQuestions = ({ quiz, onBack, onViewScores }) => {
  const [questions, setQuestions] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form] = Form.useForm();
  const [explanation, setExplanation] = useState('');
  const [searchText, setSearchText] = useState('');
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // OnlyOffice explanation document state
  const [explanationDoc, setExplanationDoc] = useState<{ doc_id: number; doc_title: string } | null>(null);
  const [docEditorVisible, setDocEditorVisible] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Document created during this modal session but not yet saved on the question —
  // deleted again if the admin cancels the modal.
  const sessionUploadedDocId = React.useRef<number | null>(null);

  useEffect(() => {
    fetchQuestions();
    fetchStudies();
  }, [quiz]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await makeGetCall(`/quiz/${quiz.quiz_id}/questions`);
      setQuestions(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch questions');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchStudies = async () => {
    try {
      const response = await makeGetCall('/quiz/studies');
      setStudies(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch studies');
      console.error(error);
    }
  };

  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    form.resetFields();
    form.setFieldsValue({
      options: [
        { id: 'A', text: '' },
        { id: 'B', text: '' }
      ]
    });
    setExplanation('');
    setSearchText('');
    setExplanationDoc(null);
    sessionUploadedDocId.current = null;
    setIsModalVisible(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setSearchText('');
    form.setFieldsValue({
      studyUID: question.question_study_uid,
      questionText: question.question_text,
      options: question.question_options,
      correctOption: question.question_correct_option,
      label: question.question_label || ''
    });
    // Decode HTML entities for proper display in ReactQuill
    const decodedExplanation = decodeHtmlEntities(question.question_explanation);
    setExplanation(decodedExplanation);
    setExplanationDoc(
      question.document
        ? { doc_id: question.document.doc_id, doc_title: question.document.doc_title }
        : question.question_document_id
          ? { doc_id: question.question_document_id, doc_title: 'Explanation document' }
          : null
    );
    sessionUploadedDocId.current = null;
    setIsModalVisible(true);
  };

  // ── Explanation document (OnlyOffice) handlers ──

  const handleDocUpload = async (file) => {
    if (!/\.docx$/i.test(file.name)) {
      message.error('Only .docx files are supported');
      return Upload.LIST_IGNORE;
    }

    setUploadingDoc(true);
    try {
      const title = file.name.replace(/\.docx$/i, '');
      const response = await createQuizDocument({ file, title });
      const doc = response.data.data;

      // A doc uploaded earlier in this same session is replaced — clean it up
      if (sessionUploadedDocId.current && sessionUploadedDocId.current !== doc.doc_id) {
        deleteQuizDocument(sessionUploadedDocId.current).catch(() => {});
      }

      sessionUploadedDocId.current = doc.doc_id;
      setExplanationDoc({ doc_id: doc.doc_id, doc_title: doc.doc_title });
      message.success('Document uploaded — opening editor');
      setDocEditorVisible(true);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to upload document');
      console.error(error);
    }
    setUploadingDoc(false);

    return false; // prevent antd default upload
  };

  const handleCreateBlankDoc = async () => {
    setUploadingDoc(true);
    try {
      const title = (form.getFieldValue('questionText') || 'Explanation').substring(0, 60);
      const response = await createQuizDocument({ title });
      const doc = response.data.data;

      if (sessionUploadedDocId.current && sessionUploadedDocId.current !== doc.doc_id) {
        deleteQuizDocument(sessionUploadedDocId.current).catch(() => {});
      }

      sessionUploadedDocId.current = doc.doc_id;
      setExplanationDoc({ doc_id: doc.doc_id, doc_title: doc.doc_title });
      setDocEditorVisible(true);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create document');
      console.error(error);
    }
    setUploadingDoc(false);
  };

  const handleRemoveDoc = () => {
    if (!explanationDoc) return;

    // Doc created in this session and never saved — delete it outright.
    // A pre-existing doc is only detached locally; the backend cleans it up
    // when the question is saved with documentId: null.
    if (sessionUploadedDocId.current === explanationDoc.doc_id) {
      deleteQuizDocument(explanationDoc.doc_id).catch(() => {});
      sessionUploadedDocId.current = null;
    }
    setExplanationDoc(null);
  };

  const handleCloseDocEditor = () => {
    setDocEditorVisible(false);
    // Flush editor content to disk early so the question save doesn't wait long
    if (explanationDoc) {
      forceSaveQuizDocument(explanationDoc.doc_id).catch(() => {});
    }
  };

  const handleModalCancel = () => {
    // Clean up a document uploaded during this session but never saved
    if (sessionUploadedDocId.current) {
      deleteQuizDocument(sessionUploadedDocId.current).catch(() => {});
      sessionUploadedDocId.current = null;
    }
    setIsModalVisible(false);
    form.resetFields();
    setExplanation('');
    setExplanationDoc(null);
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await makeDeleteCall(`/quiz/question/${questionId}`);
      message.success('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      message.error('Failed to delete question');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Make sure any open OnlyOffice editing session is flushed to disk
      // before the question (and its document reference) is saved.
      if (explanationDoc) {
        try {
          await forceSaveQuizDocument(explanationDoc.doc_id);
        } catch (e) {
          console.warn('Force save of explanation document failed:', e);
        }
      }

      const payload = {
        studyUID: values.studyUID || null,
        questionText: values.questionText,
        options: values.options,
        correctOption: values.correctOption,
        explanation: explanation,
        documentId: explanationDoc?.doc_id || null,
        label: values.label || ''
      };

      if (editingQuestion) {
        await makePutCall(`/quiz/question/${editingQuestion.question_id}`, payload);
        message.success('Question updated successfully');
      } else {
        await makePostCall(`/quiz/${quiz.quiz_id}/question`, payload);
        message.success('Question created successfully');
      }

      // Document is now owned by the question — no session cleanup needed
      sessionUploadedDocId.current = null;

      setIsModalVisible(false);
      form.resetFields();
      setExplanation('');
      setExplanationDoc(null);
      fetchQuestions();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save question');
      console.error(error);
    }
  };

  const filteredStudies = studies.filter(study =>
    study.label.toLowerCase().includes(searchText.toLowerCase())
  );

  // Distinct labels from all questions
  const distinctLabels = React.useMemo(() => {
    const labels = new Set<string>();
    for (const q of questions) {
      if (q.question_label) labels.add(q.question_label);
    }
    return Array.from(labels).sort();
  }, [questions]);

  // Filter questions by label, then group by study UID
  const studyGroups = React.useMemo(() => {
    const filtered = labelFilter
      ? questions.filter(q => q.question_label === labelFilter)
      : questions;

    const map: Record<string, { studyUID: string; patientName: string; studyLabel: string; questions: any[] }> = {};
    const FLOATING_KEY = '__floating__';
    for (const q of filtered) {
      const uid = q.question_study_uid;
      const key = uid || FLOATING_KEY;
      if (!map[key]) {
        if (uid) {
          const study = studies.find(s => s.studyUID === uid);
          map[key] = {
            studyUID: uid,
            patientName: study?.patientName || uid.substring(0, 20) + '...',
            studyLabel: study?.label || uid.substring(0, 30) + '...',
            questions: [],
          };
        } else {
          map[key] = {
            studyUID: FLOATING_KEY,
            patientName: 'Floating questions',
            studyLabel: 'Floating questions (no study attached)',
            questions: [],
          };
        }
      }
      map[key].questions.push(q);
    }
    return Object.values(map);
  }, [questions, studies, labelFilter]);


  // Parent table columns — one row per study
  const studyColumns = [
    {
      title: 'Study',
      dataIndex: 'studyLabel',
      key: 'studyLabel',
      ellipsis: true,
    },
    {
      title: 'Questions',
      key: 'questionCount',
      width: 120,
      align: 'center' as const,
      render: (_, record) => <Tag color="blue">{record.questions.length} question{record.questions.length > 1 ? 's' : ''}</Tag>
    },
    {
      title: 'Responses',
      key: 'totalResponses',
      width: 180,
      align: 'center' as const,
      render: (_, record) => {
        const total = record.questions.reduce((sum, q) => sum + (q.responseCount || 0), 0);
        const correct = record.questions.reduce((sum, q) => sum + (q.correctCount || 0), 0);
        if (total === 0) return <span style={{ color: '#999' }}>0</span>;
        return <span>{total} responses <span className="text-green-600">({Math.round((correct / total) * 100)}%)</span></span>;
      }
    },
  ];

  // Render questions as cards inside expanded row
  const renderQuestionCards = (record) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {record.questions.map((q, idx) => (
        <div
          key={q.question_id}
          style={{
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: '10px 14px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ color: '#888', fontSize: 12, fontWeight: 500 }}>Q{idx + 1}</span>
                {q.question_label && <Tag color="blue" style={{ fontSize: 10 }}>{q.question_label}</Tag>}
                <Tag style={{ fontSize: 10 }}>{q.question_options?.length || 0} options</Tag>
                {q.question_document_id && (
                  <Tag color="geekblue" style={{ fontSize: 10 }} icon={<FileWordOutlined />}>
                    Word explanation
                  </Tag>
                )}
                {q.responseCount > 0 && (
                  <span style={{ fontSize: 11, color: '#52c41a' }}>
                    {q.responseCount} responses ({Math.round((q.correctCount / q.responseCount) * 100)}% correct)
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {q.question_text}
              </div>
            </div>
            <Space size="small" style={{ flexShrink: 0 }}>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditQuestion(q)}>
                Edit
              </Button>
              <Popconfirm
                title="Delete this question and all its responses?"
                onConfirm={() => handleDeleteQuestion(q.question_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </div>
        </div>
      ))}
    </div>
  );

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} className="mb-2">
          Back to Quizzes
        </Button>
        <h2 className="text-2xl font-bold">{quiz.quiz_name} - Questions</h2>
        <p className="text-gray-500">Manage questions for this quiz</p>
      </div>

      <div className="mb-4 flex gap-2 items-center">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateQuestion}>
          Add Question
        </Button>
        <Button onClick={() => onViewScores(quiz)}>
          View Scores
        </Button>
        {distinctLabels.length > 0 && (
          <Select
            placeholder="Filter by label"
            allowClear
            value={labelFilter}
            onChange={setLabelFilter}
            style={{ width: 180 }}
          >
            {distinctLabels.map(l => (
              <Option key={l} value={l}>{l}</Option>
            ))}
          </Select>
        )}
      </div>

      <Table
        columns={studyColumns}
        dataSource={studyGroups}
        loading={loading}
        rowKey="studyUID"
        expandable={{
          expandedRowRender: renderQuestionCards,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} studies`,
          position: ['bottomCenter']
        }}
      />

      <Modal
        title={editingQuestion ? 'Edit Question' : 'Create New Question'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={900}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="studyUID"
            label="Select Study (optional)"
            extra="Leave blank to create a floating question that can be attached to a study later."
          >
            <Select
              showSearch
              allowClear
              placeholder="No study yet — leave blank for a floating question"
              onSearch={setSearchText}
              onSelect={() => setSearchText('')}
              onClear={() => setSearchText('')}
              filterOption={false}
              optionLabelProp="label"
            >
              {filteredStudies.map((study, idx) => (
                <Option
                  key={idx}
                  value={study.studyUID}
                  label={study.label}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{study.label}</span>
                    {study.existingQuestionCount > 0 && (
                      <Tag color="blue" style={{ fontSize: '10px', marginLeft: 8 }}>{study.existingQuestionCount} Q</Tag>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="label"
            label="Body Part / Label"
          >
            <Input placeholder="e.g., Chest, Abdomen, MSK, Brain" />
          </Form.Item>

          <Form.Item
            name="questionText"
            label="Question Text"
            rules={[{ required: true, message: 'Please enter question text' }]}
          >
            <TextArea
              rows={3}
              placeholder="Based on the imaging findings, what is the most likely diagnosis?"
            />
          </Form.Item>

          <Divider orientation="left">Options</Divider>

          <Form.List name="options" initialValue={[{ id: 'A', text: '' }, { id: 'B', text: '' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16, gap: 8 }}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'id']}
                      style={{ width: 60, marginBottom: 0 }}
                    >
                      <Input placeholder="A" disabled />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'text']}
                      rules={[{ required: true, message: 'Option text required' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <TextArea rows={5} placeholder="Enter detailed option text (can be several sentences)" />
                    </Form.Item>
                    {fields.length > 2 && (
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        style={{ marginTop: 8, fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                      />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add({ id: String.fromCharCode(65 + fields.length), text: '' })}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Option
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.options !== currentValues.options}
          >
            {({ getFieldValue }) => {
              const options = getFieldValue('options') || [];
              return (
                <Form.Item
                  name="correctOption"
                  label="Correct Answer(s) - Select one or more"
                  rules={[{
                    required: true,
                    message: 'Please select at least one correct answer',
                    type: 'array',
                    min: 1
                  }]}
                >
                  <Checkbox.Group>
                    <Space direction="vertical">
                      {options.map((opt, idx) => (
                        <Checkbox key={idx} value={opt?.id}>Option {opt?.id}</Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Divider orientation="left">Explanation Document (Word)</Divider>

          <Form.Item
            label="Explanation document (shown after answer submission)"
            extra="Upload the existing Word file (question, options, explanation). Open the editor, cut the question & options into the fields above, and keep only the explanation in the document — formatting, images and annotations are preserved exactly."
          >
            {explanationDoc ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  background: '#fafafa'
                }}
              >
                <FileWordOutlined style={{ fontSize: 18, color: '#2b579a' }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {explanationDoc.doc_title}
                </span>
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setDocEditorVisible(true)}
                >
                  Open Editor
                </Button>
                <Popconfirm
                  title="Remove this document from the question?"
                  onConfirm={handleRemoveDoc}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    Remove
                  </Button>
                </Popconfirm>
              </div>
            ) : (
              <Space>
                <Upload
                  accept=".docx"
                  showUploadList={false}
                  beforeUpload={handleDocUpload}
                  disabled={uploadingDoc}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingDoc}>
                    Upload Word File (.docx)
                  </Button>
                </Upload>
                <Button icon={<FileAddOutlined />} onClick={handleCreateBlankDoc} disabled={uploadingDoc}>
                  Create Blank Document
                </Button>
              </Space>
            )}
          </Form.Item>

          <Divider orientation="left">Explanation (Rich Text)</Divider>

          <Form.Item
            label="Explanation (shown after answer submission)"
            extra={explanationDoc ? 'A Word document is attached — it will be shown instead of this rich text explanation.' : undefined}
          >
            <ReactQuill
              theme="snow"
              value={explanation}
              onChange={setExplanation}
              modules={quillModules}
              placeholder="Explain the correct answer with supporting details and references..."
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            <FileWordOutlined style={{ color: '#2b579a' }} />
            <span>{explanationDoc?.doc_title || 'Explanation Document'}</span>
            <span style={{ fontSize: 12, color: '#888', fontWeight: 'normal' }}>
              — cut the question & options into the form; keep only the explanation here. Changes save automatically.
            </span>
          </Space>
        }
        visible={docEditorVisible}
        onCancel={handleCloseDocEditor}
        width="92vw"
        style={{ top: 16 }}
        bodyStyle={{ height: '82vh', padding: 0 }}
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDocEditor}>
            Done
          </Button>
        ]}
      >
        {docEditorVisible && explanationDoc && (
          <QuizDocumentEditor
            docId={explanationDoc.doc_id}
            mode="edit"
            editorType="desktop"
            height="100%"
          />
        )}
      </Modal>
    </div>
  );
};

export default ManageQuestions;
